/**
 * @file useFileUploadWithThumbnails.ts
 * @description Extended file upload hook with automatic client-side thumbnail generation
 *
 * Extends useFileUpload with instant thumbnail preview generation for images,
 * providing Claude-like UX where thumbnails appear before sending the message.
 *
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { useFileUpload, type FileUploadOptions, type FileUploadResult } from './useFileUpload';
import {
  generateClientThumbnailsBatch,
  type FileWithThumbnail
} from '../utils/thumbnailGenerator';

/**
 * Extended result including thumbnail data
 */
export interface FileUploadWithThumbnailsResult extends FileUploadResult {
  /** Files with their generated thumbnails */
  filesWithThumbnails: FileWithThumbnail[];
  /** Indicates if thumbnails are being generated */
  isGeneratingThumbnails: boolean;
}

/**
 * Hook for file upload with automatic client-side thumbnail generation
 *
 * @param onFilesSelected - Callback when files are selected
 * @param options - File upload options
 * @returns Extended file upload result with thumbnails
 *
 * @example
 * const {
 *   selectedFiles,
 *   filesWithThumbnails,  // Files with preview thumbnails
 *   isGeneratingThumbnails,
 *   triggerFileSelect
 * } = useFileUploadWithThumbnails(
 *   async (files) => {
 *     // Process files...
 *   },
 *   { maxSize: 10 * 1024 * 1024, multiple: true }
 * );
 *
 * // Display previews
 * {filesWithThumbnails.map(({ file, thumbnailDataUrl }) => (
 *   <img src={thumbnailDataUrl || defaultIcon} alt={file.name} />
 * ))}
 */
export const useFileUploadWithThumbnails = (
  onFilesSelected: (files: File[]) => Promise<void> | void,
  options: FileUploadOptions = {}
): FileUploadWithThumbnailsResult => {

  // Base file upload hook
  const baseUploadResult = useFileUpload(onFilesSelected, options);

  // Extended state for thumbnails
  const [filesWithThumbnails, setFilesWithThumbnails] = useState<FileWithThumbnail[]>([]);
  const [isGeneratingThumbnails, setIsGeneratingThumbnails] = useState(false);

  /**
   * Generate thumbnails when selectedFiles changes
   */
  useEffect(() => {
    // ✅ FIX: Track mount state to prevent setState after unmount
    let isMounted = true;

    const generateThumbnails = async () => {
      if (baseUploadResult.selectedFiles.length === 0) {
        if (isMounted) setFilesWithThumbnails([]);
        return;
      }

      console.log(`[useFileUploadWithThumbnails] Generating thumbnails for ${baseUploadResult.selectedFiles.length} files`);
      if (isMounted) setIsGeneratingThumbnails(true);

      try {
        const startTime = performance.now();

        // Generate thumbnails in parallel
        const results = await generateClientThumbnailsBatch(baseUploadResult.selectedFiles);

        const endTime = performance.now();
        const duration = Math.round(endTime - startTime);
        const successCount = results.filter(r => r.thumbnailDataUrl !== null).length;

        console.log(`[useFileUploadWithThumbnails] Generated ${successCount}/${results.length} thumbnails in ${duration}ms`);

        // ✅ FIX: Only update state if still mounted
        if (isMounted) setFilesWithThumbnails(results);
      } catch (error) {
        console.error('[useFileUploadWithThumbnails] Error generating thumbnails:', error);
        // Fallback: set files without thumbnails
        if (isMounted) {
          setFilesWithThumbnails(
            baseUploadResult.selectedFiles.map(file => ({
              file,
              thumbnailDataUrl: null,
              error: 'Thumbnail generation failed'
            }))
          );
        }
      } finally {
        // ✅ FIX: Only update state if still mounted
        if (isMounted) setIsGeneratingThumbnails(false);
      }
    };

    generateThumbnails();

    // ✅ FIX: Cleanup function to mark as unmounted
    return () => {
      isMounted = false;
    };
  }, [baseUploadResult.selectedFiles]);

  return {
    ...baseUploadResult,
    filesWithThumbnails,
    isGeneratingThumbnails
  };
};
